import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface InfluencerAccessState {
  hasInfluencerAccess: boolean;
  expiresAt: string | null;
  isExpired: boolean;
  isLoading: boolean;
}

export function useInfluencerAccess(): InfluencerAccessState {
  const { user, isHydrated } = useAuth();
  const [state, setState] = useState<InfluencerAccessState>({
    hasInfluencerAccess: false,
    expiresAt: null,
    isExpired: false,
    isLoading: true,
  });

  const fetchAccess = useCallback(async () => {
    if (!isHydrated) return;
    if (!user) {
      setState({ hasInfluencerAccess: false, expiresAt: null, isExpired: false, isLoading: false });
      return;
    }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('influencer_access, influencer_access_expires_at')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      const hasAccess = data?.influencer_access ?? false;
      const expiresAt = data?.influencer_access_expires_at ?? null;
      const isExpired = expiresAt ? new Date(expiresAt) < new Date() : false;

      setState({
        hasInfluencerAccess: hasAccess && !isExpired,
        expiresAt,
        isExpired,
        isLoading: false,
      });
    } catch (err) {
      console.error('[InfluencerAccess] Error:', err);
      setState({ hasInfluencerAccess: false, expiresAt: null, isExpired: false, isLoading: false });
    }
  }, [isHydrated, user?.id]);

  useEffect(() => {
    fetchAccess();
  }, [fetchAccess]);

  return state;
}
